import { useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ArrowLeft, BookOpenCheck, Check, Copy, FileText, Pencil } from 'lucide-react'
import type { Document, Role } from '@agentwiki/shared'
import { SourceAuthorityReview } from '../components/authority/source-authority-review'
import { DocumentProvenanceBanner } from '../components/editor/document-provenance-banner'
import { DocumentTableOfContents } from '../components/editor/document-table-of-contents'
import {
  DocumentReaderBreadcrumbs,
  DocumentReaderMetadata,
} from '../components/reader/document-reader-context'
import { DocumentReaderStatusNotice } from '../components/reader/document-reader-status-notice'
import { DocumentMarkdown } from '../components/shared/document-markdown'
import { SourceConflictResolutionPanel } from '../components/source-conflicts/source-conflict-resolution-panel'
import { extractDocumentHeadings } from '../lib/document-reader'
import { documentReadableContent, normalizeReaderMarkdown } from '../lib/document-readable-content'
import {
  formatRelativeSyncTime,
  formatSourcePath,
  getDocumentKindInfo,
  getDocumentOwnershipInfo,
} from '../lib/document-kind'
import { getSafeDocumentReturnTarget } from '../lib/document-return-continuity'
import { documentEditPath } from '../lib/document-route'
import { getDocumentSourceAuthorityMetadata } from '../lib/source-authority-metadata'
import { cn } from '../lib/utils'
import { useAuthorityExpiryRefresh } from '../hooks/use-authority-expiry-refresh'

interface DocumentReaderPageProps {
  doc: Document
  canEdit: boolean
  canResolveConflict?: boolean
  role?: Role | null
}

export function DocumentReaderPage({
  doc,
  canEdit,
  canResolveConflict = false,
  role = null,
}: DocumentReaderPageProps) {
  const [copied, setCopied] = useState(false)
  const location = useLocation()
  const returnTarget = getSafeDocumentReturnTarget(location.state)
  const content = normalizeReaderMarkdown(documentReadableContent(doc), doc.title)
  const headings = useMemo(() => extractDocumentHeadings(content), [content])
  const info = getDocumentKindInfo(doc)
  const ownership = getDocumentOwnershipInfo(doc)
  const source = doc.sourceProvenance
  const KindIcon = source ? BookOpenCheck : FileText
  const authorityMetadata = getDocumentSourceAuthorityMetadata(doc)
  useAuthorityExpiryRefresh([authorityMetadata?.authorityExpiresAt])

  const copyLink = async () => {
    await navigator.clipboard.writeText(window.location.href)
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1800)
  }

  return (
    <div className="aw-doc-editor aw-knowledge-reader flex h-full flex-col">
      <a href="#document-content" className="aw-reader-skip-link">
        Skip to document content
      </a>
      <div className="aw-doc-body flex-1 overflow-y-auto" aria-label="Read document">
        <article className="aw-doc-body-inner">
          <div className="aw-doc-readable-column">
            <header className="aw-doc-editor-head aw-knowledge-reader-head">
              <div className="aw-knowledge-reader-topline">
                <Link to={returnTarget.href} className="aw-knowledge-reader-back">
                  <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
                  Back to {returnTarget.label}
                </Link>
                <div className="aw-reader-actions">
                  <button
                    type="button"
                    className="aw-reader-action"
                    onClick={() => void copyLink()}
                  >
                    {copied ? (
                      <Check className="h-4 w-4" aria-hidden />
                    ) : (
                      <Copy className="h-4 w-4" aria-hidden />
                    )}
                    {copied ? 'Copied' : 'Copy link'}
                  </button>
                  {canEdit && (
                    <Link
                      to={documentEditPath(doc)}
                      state={location.state}
                      className="aw-reader-action primary"
                    >
                      <Pencil className="h-4 w-4" aria-hidden />
                      Edit
                    </Link>
                  )}
                  <span className="sr-only" aria-live="polite">
                    {copied ? 'Document link copied' : ''}
                  </span>
                </div>
              </div>

        <DocumentReaderBreadcrumbs doc={doc} />

              <div className="aw-knowledge-reader-title-block">
                <div className={cn('aw-knowledge-reader-kind', `is-${ownership.ownership}`)}>
                  <KindIcon className="h-4 w-4" aria-hidden />
                  <span>{ownership.label}</span>
                </div>
                <h1>{doc.title || 'Untitled'}</h1>
                <p>
                  {doc.description ||
                    (source?.sourcePath
                      ? formatSourcePath(source.sourcePath, 120)
                      : 'Managed directly in AgentWiki')}
                </p>
                <span className="aw-reader-updated">
                  Updated {formatRelativeSyncTime(doc.updatedAt)}
                </span>
              </div>

              <DocumentReaderMetadata doc={doc} />

              {source && <DocumentProvenanceBanner doc={doc} />}
              <DocumentReaderStatusNotice doc={doc} />
            </header>

            {source && authorityMetadata && (
              <SourceAuthorityReview
                role={role}
                documentId={doc.id}
                documentTitle={doc.title}
                sourceId={authorityMetadata.sourceId}
                initialMetadata={authorityMetadata}
              />
            )}

            {source && info.kind === 'conflict' && canResolveConflict && (
              <SourceConflictResolutionPanel documentId={doc.id} />
            )}

            <DocumentTableOfContents
              headings={headings}
              className="aw-doc-toc-mobile"
              useHashLinks
              collapsible
              backTo={returnTarget.href}
              backLabel={returnTarget.label}
            />

            <section
              id="document-content"
              tabIndex={-1}
              className="aw-doc-reader-surface aw-knowledge-reader-surface"
              aria-label="Read-only content"
            >
              <div className="aw-knowledge-reader-markdown">
                {content ? (
                  <DocumentMarkdown content={content} headings={headings} />
                ) : (
                  <p>This document does not have content yet.</p>
                )}
              </div>
            </section>
          </div>

          <DocumentTableOfContents
            headings={headings}
            className="aw-doc-toc-desktop"
            useHashLinks
            backTo={returnTarget.href}
            backLabel={returnTarget.label}
          />
        </article>
      </div>
    </div>
  )
}
